import React, { useContext, useEffect, useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Layout from "../components/shared/Layout";
import Home from "../pages/home/Home";
import ScrollToTop from "./ScrollTop";
import SignupPage from "../pages/signup/Page";
import AppLinkPage from "../pages/app-link/Page";
import Login from "../pages/login/Page";
import InviteCleaner from "../pages/invite-cleaner/Page";
import InvitationStatus from "../pages/invitation-status/Page";
import PropertyScreen from "../pages/property-screen/Page";
import Invoice from "../pages/invoice/Page";
import SendInvitation from "../pages/send-invitation/Page";
import GuestStayForm from "../pages/guest-stays/components/Page";
import UpcomingStays from "../pages/guest-stays/UpcomingStays";
import Dashboard from "../pages/Dashboard/Page";
import Task from "../pages/task-type/Page";
import Lodgify from "../pages/Lodgify-Integration/Page";
import LodgifyConnect from "../pages/Lodgify-connect/Page";
import LodgifyImportProperty from "../pages/Lodgify-Import-Property/Page";
import LodgifyImportHKProperty from "../pages/Lodgify-Import-HKProperty/Page";
import Verify from "../pages/verify/Page";
import VerifyCleaner from "../pages/invite-cleaner/VerifyCleaner";
import { getAccessToken } from "../storage/storage";
import Settings from "../pages/settings/Page";
import ForgotPassword from "../pages/forgot-password/Page";
import ResetLink from "../pages/forgot-password/ResetLink";
import Swal from "sweetalert2";
import GlobalContext from "../context/GlobalContext";
import TaskSheet from "../pages/guest-stays/components/TaskSheet";
import OtherTask from "../pages/other-task/Page";
import OtherInvoiceDetail from "../pages/other-task/InvoiceDetail";
import OtherInvoice from "../pages/other-task/components/OtherInvoice";
import OtherTaskSheets from "../pages/other-task/OtherTaskSheets";
import axios from "axios";
import Cookies from "js-cookie";
import LoadingSpinner from "../components/shared/LoadingSpinner"; // ✅ Spinner component
import "./Routing.css";
import PublicLayout from "../layout/PublicLayout";
import CleanerMobileTaskSheet from "../pages/cleaner-mobile-task-sheet/Page";

const Routing = () => {
  const { userData } = useContext(GlobalContext);
  const navigate = useNavigate();
  const token = getAccessToken();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const location = useLocation();
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  useEffect(() => {
    if (token) {
      axios
        .get(`${BASE_URL}/api/profile/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        })
        .then((response) => {
          const data = response.data;
          sessionStorage.setItem(
            "companyInfo",
            JSON.stringify(data.company_information_added)
          );
          sessionStorage.setItem(
            "PropertyData",
            JSON.stringify(data.import_lodgify)
          );
          sessionStorage.setItem(
            "CleanerData",
            JSON.stringify(data.cleaner_data)
          );
          setIsDataLoaded(true);
        })
        .catch((error) => {
          console.error("Error fetching profile data:", error);
          setIsDataLoaded(true);
        });
    } else {
      setIsDataLoaded(true);
    }
  }, [token, BASE_URL]);

  const companyInfo = JSON.parse(sessionStorage.getItem("companyInfo"));

  useEffect(() => {
    if (!token) {
      const publicRoutes = [
        "/login",
        "/signup",
        "/app-link",
        "/cleaner-mobile-task-sheet",
        "/forgot-password",
        "/reset-password",
        "/verify",
        "/verify-cleaner",
      ];

      const currentPath = location.pathname;
      const isPublicRoute = publicRoutes.some((route) =>
        currentPath.startsWith(route)
      );

      if (!isPublicRoute) {
        // Store the intended route before redirecting to login
        sessionStorage.setItem("intendedRoute", currentPath);
        navigate("/login");
      }
    } else {
      if (location.pathname === "/login" || location.pathname === "/signup") {
        navigate("/");
      }
    }
  }, [token, navigate, location.pathname]);

  // Handle intended route redirects after login
  useEffect(() => {
    if (isDataLoaded && token && location.pathname === "/") {
      // Add a small delay to ensure sessionStorage operations are complete
      const timeoutId = setTimeout(() => {
        const intendedRoute = sessionStorage.getItem("intendedRoute");
        const currentCompanyInfo = JSON.parse(
          sessionStorage.getItem("companyInfo")
        );
        if (intendedRoute) {
          // Clear the stored route and redirect to it
          sessionStorage.removeItem("intendedRoute");
          navigate(intendedRoute);
        } else {
          // Clear any stale intended route since we're not using it
          if (sessionStorage.getItem("intendedRoute")) {
            sessionStorage.removeItem("intendedRoute");
          }
          if (currentCompanyInfo === true) {
            navigate("/dashboard");
          } else {
            console.log("✅ No intended route, staying on home");
            // For users without company info, they stay on "/" which renders <Home />
          }
        }
      }, 10); // Small delay to ensure sessionStorage is ready
      return () => clearTimeout(timeoutId);
    }
  }, [isDataLoaded, token, companyInfo, location.pathname, navigate]);

  useEffect(() => {
    if (userData) {
      Swal.fire({
        icon: "success",
        title: `Welcome, ${userData.first_name} ${userData.last_name}!`,
        text: "We're glad to have you here.",
        timer: 3000,
        showConfirmButton: false,
        position: "center",
      });
    }
  }, [userData]);

  // Show spinner until data is fully loaded
  if (!isDataLoaded) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {/* {isPublicRoute && <Header />} */}
      <Routes>
        {/* Public routes */}
        <Route element={<PublicLayout />}>
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/app-link" element={<AppLinkPage />} />
          <Route
            path="/cleaner-mobile-task-sheet/*"
            element={<CleanerMobileTaskSheet />}
          />
          <Route path="/login" element={<Login />} />
          <Route path="/verify/:id" element={<Verify />} />
          <Route path="/verify-cleaner/:id" element={<VerifyCleaner />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/reset-password/:uidb64/:token"
            element={<ResetLink />}
          />
        </Route>

        {/* Protected routes */}
        {token ? (
          <>
            {companyInfo === true ? (
              <Route path="/" element={<Layout />}>
                <Route index element={<Dashboard />} />
                <Route path="*" element={<Dashboard />} />
              </Route>
            ) : (
              <>
                <Route path="/" element={<Home />} />
                <Route path="*" element={<Home />} />
              </>
            )}
            <Route path="/" element={<Layout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/invite-cleaner" element={<InviteCleaner />} />
              <Route path="/invitation-status" element={<InvitationStatus />} />
              <Route path="/property" element={<PropertyScreen />} />
              <Route path="/invoice/:guestStayId" element={<Invoice />} />
              <Route path="/task-sheet/:id" element={<TaskSheet />} />
              <Route path="/send-invitation" element={<SendInvitation />} />
              <Route path="/stays/add-stays" element={<GuestStayForm />} />
              <Route path="/stays" element={<UpcomingStays />} />
              <Route path="/task" element={<Task />} />
              <Route path="/lodgify/:case?" element={<Lodgify />} />
              <Route
                path="/lodgify-connect/:case?"
                element={<LodgifyConnect />}
              />
              <Route
                path="/lodgify-import-property"
                element={<LodgifyImportProperty />}
              />
              <Route
                path="/lodgify-import-hkproperty"
                element={<LodgifyImportHKProperty />}
              />
              <Route path="/settings/:currentTab" element={<Settings />} />
              <Route path="/other-tasks" element={<OtherTask />} />
              <Route
                path="/other-tasksheets/:id"
                element={<OtherTaskSheets />}
              />
              <Route
                path="/task-invoice/:guestStayId"
                element={<OtherInvoice />}
              />
            </Route>
            <Route
              path="/invoice-detail/:invoiceId"
              element={<OtherInvoiceDetail />}
            />
          </>
        ) : (
          <Route path="*" element={<Login />} />
        )}
      </Routes>
      <ScrollToTop />
    </>
  );
};

export default Routing;
